// pod-difference-modal.js — "What's the difference?" right-side drawer custom element.
//
// Opened by any [data-pods-diff-open] button on the page (pods-switcher diff link).
// Closes via the [data-close] × button, backdrop overlay click, or Escape key.
//
// Visibility is class-driven (.is-open) so the CSS slide transition works —
// toggling [hidden] causes instant display:none which breaks the animation.
//
// Focus management delegates to Motto's global trapFocus / removeTrapFocus (window.*),
// with a plain .focus() fallback if those globals are unavailable.

class PodDifferenceModal extends HTMLElement {
  connectedCallback() {
    this._trigger = null;
    this._abort = new AbortController();
    const { signal } = this._abort;

    // × close button + backdrop overlay both carry [data-close]
    this.querySelectorAll('[data-close]').forEach((el) => {
      el.addEventListener('click', () => this.close(), { signal });
    });

    // Escape key
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open'))
          this.close();
      },
      { signal }
    );

    // Theme Editor — open when section/block is selected so editors can preview content
    document.addEventListener(
      'shopify:section:select',
      (e) => {
        if (this.closest('.shopify-section')?.id.includes(e.detail.sectionId))
          this.open();
      },
      { signal }
    );
    document.addEventListener(
      'shopify:section:deselect',
      (e) => {
        if (this.closest('.shopify-section')?.id.includes(e.detail.sectionId))
          this.close();
      },
      { signal }
    );

    // Wire up triggers already in the DOM, then re-wire after any section load
    // (Theme Editor injects sections dynamically on customiser navigation).
    this._bindTriggers(signal);
    document.addEventListener(
      'shopify:section:load',
      () => this._bindTriggers(signal),
      { signal }
    );
  }

  // Attach click handlers to every [data-pods-diff-open] button.
  // _podDiffBound guard prevents stacking duplicate listeners on already-bound buttons.
  _bindTriggers(signal) {
    document.querySelectorAll('[data-pods-diff-open]').forEach((btn) => {
      if (btn._podDiffBound) return;
      btn._podDiffBound = true;
      btn.addEventListener(
        'click',
        () => {
          this._trigger = btn;
          this.open();
        },
        { signal }
      );
    });
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pod-modal-open');

    const closeBtn = this.querySelector('.pod-difference-modal__close');
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(this, closeBtn);
    } else if (closeBtn) {
      closeBtn.focus();
    }
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pod-modal-open');

    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(this._trigger);
    } else if (this._trigger) {
      this._trigger.focus();
    }
    this._trigger = null;
  }

  disconnectedCallback() {
    this._abort?.abort();
  }
}

if (!customElements.get('pod-difference-modal')) {
  customElements.define('pod-difference-modal', PodDifferenceModal);
}
